import Cookies from 'js-cookie';
import jwt_decode, { JwtPayload } from 'jwt-decode';
import { toggleRecoilUserSync } from '../app/store/recoilSyncSlice';
import {
  ACCESS_TOKEN,
  ACCESS_TOKEN_COOKIE_EXPIRE_MS,
  REFRESH_TOKEN,
} from '../constants';
import { IUserSettings } from '../recoil/selectors/permissionsSelector';
import { getToken } from '../services/auth/refreshTokenAjax';

/**
 * If token has expired then return new access token otherwise return undefined
 */
export async function checkAndGetNewAccessToken(): Promise<{
  accessToken: string | undefined;
  refreshed: boolean;
  expiresInTimestamp: number | undefined;
}> {
  const accessToken = getCachedAccessToken();
  const currentEpoch = Math.floor(new Date().getTime() / 1000);
  let userToken: JwtPayload | null = null;
  if (accessToken) {
    try {
      userToken = jwt_decode(accessToken) as JwtPayload | null;
    } catch (exception) {
      // skip error
      // if (!(exception instanceof InvalidTokenError)) {
      //   throw exception;
      // }
    }
  }

  // token does not exists or has expired (5 second buffor)
  if (!userToken?.exp || userToken.exp <= currentEpoch + 5) {
    let refreshToken = getCachedRefreshToken();
    const { store } = await import('src/app/store');
    // @todo: figure a way to logout when refreshToken given problem below
    // NOTE: Do not logout on refreshToken empty since the initial call to
    // checkAndGetNewAccessToken.ts after login will not have a refresh token
    // resulting in login page being seen twice.
    const response = await getToken({ refreshToken: refreshToken ?? '' });

    // must import dynamically since store will is not available when this file is loaded into memory
    // const { store } = await import('../app/store');

    // check refresh token in local storage again because it been be removed after getToken response
    refreshToken = getCachedRefreshToken();
    // refresh token if new token successfully gotten
    if (response.success && 'expiresInTimestamp' in response) {
      const expires = new Date(
        response.expiresInTimestamp ||
          Date.now() + ACCESS_TOKEN_COOKIE_EXPIRE_MS
      );
      Cookies.set(ACCESS_TOKEN, response.accessToken, {
        path: '/',
        expires,
        // domain: 'localhost',
      });

      // ENG-8377: update userAtom with new userSettings from decoded access token
      // we use a redux action to trigger the update in another function with a hook
      // this is because we use recoil to control the state of the userAtom and recoil
      // must be used in a function with a hook, which checkAndGetNewAccessToken is not
      const newUserSettings = jwt_decode(response.accessToken) as IUserSettings;
      store.dispatch(
        toggleRecoilUserSync({
          ...newUserSettings,
          accessToken: response.accessToken,
          apiKey: newUserSettings.apikey,
        })
      );

      // dispatch(toggleRecoilUserSync({ payload: newUserSettings }));

      // store.dispatch(
      //   saveAccessTokenJson({
      //     accessToken: response.accessToken,
      //     expiresInTimestamp: response.expiresInTimestamp,
      //   }),
      // );
      return {
        accessToken: response.accessToken,
        refreshed: true,
        expiresInTimestamp: response.expiresInTimestamp,
      };
    } else if (
      response.logout ||
      (!sessionStorage.getItem('login-in-progress') && !refreshToken)
    ) {
      // store.dispatch(saveAccessTokenJson());
      Cookies.remove(ACCESS_TOKEN, { path: '/' });
    }
  }

  return {
    accessToken: accessToken,
    refreshed: false,
    expiresInTimestamp: undefined,
  };
}

/**
 * Retrieve refresh token
 */
export function getCachedRefreshToken() {
  return localStorage.getItem(REFRESH_TOKEN);
}

/**
 * Set or clear refresh token
 */
export function setCachedRefreshToken(refreshTokenString: string | undefined) {
  if (refreshTokenString) {
    localStorage.setItem(REFRESH_TOKEN, refreshTokenString);
  } else {
    localStorage.removeItem(REFRESH_TOKEN);
  }
}

/**
 * Retrieve access token from localStorage when ACCESS_TOKEN_EXPIRE_TIMESTAMP greater than now
 */
export function getCachedAccessToken() {
  return Cookies.get(ACCESS_TOKEN);
  // return Number(localStorage.getItem(ACCESS_TOKEN_EXPIRE_TIMESTAMP)) >
  //   Date.now()
  //   ? localStorage.getItem(ACCESS_TOKEN) ?? undefined
  //   : undefined;
}

/**
 * Set or remove access token from localStorage
 * NOTE: Cookies are not guaranteed to work with some anti-virus or other similar type of software
 */
export async function setCachedAccessToken(
  accessTokenJson: IAccessTokenJson | undefined
) {
  if (accessTokenJson) {
    // convert to Date
    const expires = new Date(
      accessTokenJson.expiresInTimestamp ||
        Date.now() + ACCESS_TOKEN_COOKIE_EXPIRE_MS
    );

    // localStorage.setItem(ACCESS_TOKEN, accessTokenJson.accessToken);
    // localStorage.setItem(ACCESS_TOKEN_EXPIRE_TIMESTAMP, String(expires.getTime())); // prettier-ignore
    // // only used to debug timestamp
    // localStorage.setItem(ACCESS_TOKEN_EXPIRE_DEBUG, expires.toString()); // prettier-ignore

    // set expiration to less than access token expiration on server
    Cookies.set(ACCESS_TOKEN, accessTokenJson.accessToken, {
      path: '/',
      expires,
      // domain: 'localhost',
    });
  } else {
    Cookies.remove(ACCESS_TOKEN, { path: '/' });
  }
}

export interface IAccessTokenJson {
  /** Access token for authentication to GraphQL server */
  accessToken: string;
  /** Unix epoch in milliseconds instead of seconds */
  expiresInTimestamp: number;
}
